import {
  Box,
  DarkTooltip,
  Stack,
  Text,
  useMediaQuery,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import { useAccount, useSession } from '@ltvco/refresh-lib/v1';
import { Menu, MenuOpen } from '@mui/icons-material';
import { type ReactNode, useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BrandLogoChat } from 'components/DeepSearch/components/BrandLogoChat';
import {
  ChatPageBoxContainer,
  ChatPageSidebarContainer,
  MenuIcon,
  NewChatButton,
} from 'components/DeepSearch/components/components';
import { HelpModal } from 'components/DeepSearch/components/HelpModal';
import { ProfileModal } from 'components/DeepSearch/components/ProfileModal';
import { ProfileSection } from 'components/DeepSearch/components/ProfileSection';
import { DeepSearchConversationList } from 'components/DeepSearch/components/Sidebar/DeepSearchConversationList';
import {
  DEEPSEARCH_MOBILE_MEDIA_QUERY,
  DEEPSEARCH_ROUTES,
  FEEDBACK_FORM_URL,
} from 'components/DeepSearch/constants';
import {
  DeepSearchContext,
  DeepSearchLayoutContext,
} from 'components/DeepSearch/context';
import { thinScrollbar } from 'components/DeepSearch/styleMixins';
import { useLimitedPlanInfo } from 'utils/useLimitedPlanInfo';

import {
  AccountNavIcon,
  CloseSidebarButton,
  MobileHeader,
  MobileNavItemButton,
  MobileProfileBox,
  NavIconSlot,
  NewChatAddIcon,
  RailToggleButton,
  ReportLibraryNavIcon,
  ScrollableConversationBox,
  SectionTitle,
  SidebarAvatar,
  SidebarColumn,
  SidebarLabelText,
  SidebarRow,
  SidebarRowLabel,
  SidebarStaticRow,
} from './DeepSearchSidebar.styles';

interface DeepSearchSidebarProps {
  onRecentReportsClick?: () => void;
  onAccountClick?: () => void;
  onUpgradePlanClick?: () => void;
  onLogout?: () => void;
}

// Single source of truth for nav labels so desktop and mobile cannot drift.
const NAV = {
  newChat: 'New Chat',
  reportLibrary: 'Report Library',
  account: 'Account',
} as const;

const MobileNavItem = ({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick?: () => void;
}) => (
  <MobileNavItemButton type="button" onClick={onClick}>
    {children}
  </MobileNavItemButton>
);

const SidebarNavRow = ({
  icon,
  label,
  onClick,
  collapsed,
}: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  collapsed: boolean;
}) => (
  <DarkTooltip title={collapsed ? label : ''} placement="right">
    <SidebarRow onClick={onClick}>
      <NavIconSlot>{icon}</NavIconSlot>
      <SidebarRowLabel collapsed={collapsed}>
        <SidebarLabelText className="sidebar-label-text">
          {label}
        </SidebarLabelText>
      </SidebarRowLabel>
    </SidebarRow>
  </DarkTooltip>
);

export const DeepSearchSidebar = ({
  onRecentReportsClick,
  onAccountClick,
  onUpgradePlanClick,
  onLogout,
}: DeepSearchSidebarProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(DEEPSEARCH_MOBILE_MEDIA_QUERY);
  const navigate = useNavigate();

  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [openHelpModal, setOpenHelpModal] = useState(false);
  const [openProfileModal, setOpenProfileModal] = useState(false);

  const { startNewConversation, isLoadingHistory, isErrorHistory } =
    useContext(DeepSearchContext);
  const { sidebarCollapsed: collapsed, setSidebarCollapsed: setCollapsed } =
    useContext(DeepSearchLayoutContext);
  const { reportsRun, reportLimit, reportsRunPercentage, isLimitedUser } =
    useLimitedPlanInfo();
  const {
    session: { isAuthenticated },
  } = useSession();
  const { data: account } = useAccount(isAuthenticated);
  const profileRef = useRef<HTMLDivElement>(null);

  const getInitial = () => {
    const info = account?.account?.user_info;
    if (info?.first_name) {
      return info.first_name[0].toUpperCase();
    }
    if (info?.last_name) {
      return info.last_name[0].toUpperCase();
    }
    if (info?.email) {
      return info.email[0].toUpperCase();
    }
    return 'U';
  };

  const closeMobileSidebar = () => {
    // Trigger the slide-out; the drawer unmounts on its animationend (below),
    // not on a fixed timer — a timer races the animation start and can unmount
    // the drawer mid-slide.
    setIsAnimatingOut(true);
  };

  const handleNewChatClick = () => {
    startNewConversation();
    navigate(DEEPSEARCH_ROUTES.chat);
    if (isMobile) {
      closeMobileSidebar();
    }
  };

  const handleRecentReportsClick = () => {
    onRecentReportsClick?.();
    if (isMobile) {
      closeMobileSidebar();
    }
  };

  const handleAccountClick = () => {
    onAccountClick?.();
    if (isMobile) {
      closeMobileSidebar();
    }
  };

  const handleUpgradePlanClick = () => {
    onUpgradePlanClick?.();
  };

  // The rail only renders on desktop (width > 1024), so no extra breakpoint
  // gate is needed — gating on useScreenSize.isDesktop (>=1200) made the toggle
  // a no-op in the 1025–1199px band.
  const handleToggleCollapsed = (value: boolean) => {
    setCollapsed(value);
  };

  const handleProfileClick = () => {
    setOpenProfileModal(true);
  };

  const handleHelpModalClick = () => {
    setOpenHelpModal(true);
  };

  const handleFeedbackFormClick = () => {
    window.open(FEEDBACK_FORM_URL, '_blank', 'noopener,noreferrer');
  };

  // Report usage now lives in the ProfileModal (see reportUsage prop below),
  // so the footer is just the account + profile rows on both mobile and desktop.
  const mobileFooter = (
    <>
      <MobileNavItem onClick={handleAccountClick}>
        <NavIconSlot>
          <AccountNavIcon />
        </NavIconSlot>
        <SidebarLabelText>{NAV.account}</SidebarLabelText>
      </MobileNavItem>
      <MobileProfileBox ref={profileRef} onClick={handleProfileClick}>
        <Stack direction="row" spacing={2} alignItems="center">
          <SidebarAvatar>{getInitial()}</SidebarAvatar>
          <ProfileSection account={account} />
        </Stack>
      </MobileProfileBox>
    </>
  );

  const modals = (
    <>
      <HelpModal
        open={openHelpModal}
        onClose={() => setOpenHelpModal(false)}
        onStartSearching={handleNewChatClick}
      />
      <ProfileModal
        open={openProfileModal}
        onClose={() => {
          setOpenProfileModal(false);
          if (isMobile) {
            closeMobileSidebar();
          }
        }}
        onAccountClick={handleAccountClick}
        onUpgradePlanClick={handleUpgradePlanClick}
        onHelpClick={handleHelpModalClick}
        onFeedbackClick={handleFeedbackFormClick}
        onLogout={onLogout}
        reportUsage={
          isLimitedUser
            ? {
                reportsRun,
                reportsLimit: reportLimit,
                progressValue: reportsRunPercentage,
              }
            : undefined
        }
      />
    </>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <BrandLogoChat width="100px" height="16px" />
          <MenuIcon
            // Enlarge the tap target without resizing the glyph or shifting the
            // layout: content-box keeps the icon 24px, padding adds hit area,
            // and the matching -8px margin cancels it in the layout box.
            sx={{
              padding: '8px',
              margin: '-8px',
              boxSizing: 'content-box',
            }}
            onClick={() => {
              if (isMobileOpen) {
                closeMobileSidebar();
              } else {
                setIsMobileOpen(true);
              }
            }}
          />
        </MobileHeader>

        {(isMobileOpen || isAnimatingOut) && (
          <ChatPageBoxContainer
            position="fixed"
            top={0}
            left={0}
            width="100%"
            height="100dvh"
            boxShadow="4px 0 8px rgba(0, 0, 0, 0.15)"
            zIndex={1000}
            isAnimatingOut={isAnimatingOut}
            // Hold the final keyframe after the slide finishes. Without this the
            // animation's default fill-mode (none) reverts the drawer to
            // translateX(0) — full screen — for the frame between animationend
            // and React unmounting it, which flashes the drawer back on close.
            sx={{
              animationFillMode: 'forwards',
              display: 'flex',
              flexDirection: 'column',
            }}
            onAnimationEnd={(event) => {
              // Unmount only when the container's own slide-out finishes.
              // target === currentTarget ignores child animations bubbling up,
              // and the isAnimatingOut guard ignores the open (slideIn) end.
              if (event.target === event.currentTarget && isAnimatingOut) {
                setIsMobileOpen(false);
                setIsAnimatingOut(false);
              }
            }}
          >
            {/* Reuse MobileHeader so the open drawer's logo + close icon match
                the collapsed header exactly (same padding, same position) —
                toggling the drawer doesn't move them. */}
            <MobileHeader sx={{ backgroundColor: 'background.default' }}>
              <BrandLogoChat width="100px" height="16px" />
              <CloseSidebarButton
                type="button"
                aria-label="Close sidebar"
                onClick={closeMobileSidebar}
              >
                <MenuOpen sx={{ marginTop: '0 !important' }} />
              </CloseSidebarButton>
            </MobileHeader>

            <Stack
              spacing={3}
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
                padding: '16px 24px 24px',
              }}
            >
              <Stack spacing={4} sx={{ flexShrink: 0 }}>
                <MobileNavItem onClick={handleNewChatClick}>
                  <NewChatButton variant="contained">
                    <NewChatAddIcon />
                  </NewChatButton>
                  <SidebarLabelText>{NAV.newChat}</SidebarLabelText>
                </MobileNavItem>
                <MobileNavItem onClick={handleRecentReportsClick}>
                  <NavIconSlot>
                    <ReportLibraryNavIcon />
                  </NavIconSlot>
                  <SidebarLabelText>{NAV.reportLibrary}</SidebarLabelText>
                </MobileNavItem>
              </Stack>

              <Stack
                spacing={0.5}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <SectionTitle
                  sx={{ marginTop: '8px', marginBottom: 2, flexShrink: 0 }}
                >
                  Recents
                </SectionTitle>
                <Stack
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    bgcolor: 'background.default',
                    // Same thin grey scrollbar as the desktop Recents list.
                    ...thinScrollbar(theme),
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      height: 16,
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      bgcolor: 'background.default',
                    }}
                  />
                  {isLoadingHistory ? (
                    <Text
                      sx={{ px: 1, color: 'text.secondary', fontSize: '12px' }}
                    >
                      Loading...
                    </Text>
                  ) : isErrorHistory ? (
                    <Text sx={{ px: 1, color: 'error.main', fontSize: '12px' }}>
                      Failed to load history
                    </Text>
                  ) : (
                    <Box sx={{ flexShrink: 0, minHeight: 'min-content' }}>
                      <DeepSearchConversationList
                        collapsed={false}
                        onConversationClick={closeMobileSidebar}
                      />
                    </Box>
                  )}
                </Stack>
              </Stack>

              <Stack
                spacing={2.5}
                sx={{
                  flexShrink: 0,
                  paddingBottom: 'env(safe-area-inset-bottom)',
                }}
              >
                {mobileFooter}
              </Stack>
            </Stack>
          </ChatPageBoxContainer>
        )}

        {modals}
      </>
    );
  }

  return (
    <ChatPageSidebarContainer>
      <SidebarColumn collapsed={collapsed}>
        <Stack spacing={4} sx={{ flex: 1, minHeight: 0 }}>
          <SidebarStaticRow
            sx={{
              // Pin to the logo's height so the collapsed (toggle-only) state
              // keeps the same header height — the rail then only changes
              // horizontally, with no vertical jump of the rows below.
              minHeight: '40px',
              justifyContent: collapsed ? 'flex-start' : 'space-between',
              paddingRight: collapsed ? 0 : '4px',
            }}
          >
            {collapsed ? (
              <NavIconSlot>
                <DarkTooltip title="Expand" placement="right">
                  <RailToggleButton
                    aria-label="Expand sidebar"
                    onClick={() => handleToggleCollapsed(false)}
                  >
                    <Menu />
                  </RailToggleButton>
                </DarkTooltip>
              </NavIconSlot>
            ) : (
              <>
                <DarkTooltip title="Back to BeenVerified" placement="bottom">
                  <SidebarRowLabel collapsed={collapsed}>
                    <BrandLogoChat width="108px" height="40px" />
                  </SidebarRowLabel>
                </DarkTooltip>
                <RailToggleButton
                  aria-label="Collapse sidebar"
                  onClick={() => handleToggleCollapsed(true)}
                  // Right-aligned here: cancel the button's right padding so the
                  // icon sits at the edge instead of 8px inside it.
                  sx={{ marginRight: '-8px' }}
                >
                  <MenuOpen />
                </RailToggleButton>
              </>
            )}
          </SidebarStaticRow>

          <SidebarNavRow
            icon={
              <NewChatButton
                variant="contained"
                sx={{
                  '&:hover': { backgroundColor: theme.palette.button.dark },
                }}
              >
                <NewChatAddIcon />
              </NewChatButton>
            }
            label={NAV.newChat}
            onClick={handleNewChatClick}
            collapsed={collapsed}
          />

          <SidebarNavRow
            icon={<ReportLibraryNavIcon />}
            label={NAV.reportLibrary}
            onClick={handleRecentReportsClick}
            collapsed={collapsed}
          />

          <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
            <SidebarRowLabel collapsed={collapsed}>
              <SectionTitle>Recents</SectionTitle>
            </SidebarRowLabel>

            <ScrollableConversationBox collapsed={collapsed}>
              {isLoadingHistory ? (
                <Text sx={{ px: 1, color: 'text.secondary', fontSize: '12px' }}>
                  Loading...
                </Text>
              ) : isErrorHistory ? (
                <Text sx={{ px: 1, color: 'error.main', fontSize: '12px' }}>
                  Failed to load history
                </Text>
              ) : (
                <DeepSearchConversationList collapsed={collapsed} />
              )}
            </ScrollableConversationBox>
          </Stack>
        </Stack>

        <Stack spacing={3}>
          <SidebarNavRow
            icon={<AccountNavIcon />}
            label={NAV.account}
            onClick={handleAccountClick}
            collapsed={collapsed}
          />

          <SidebarStaticRow
            ref={profileRef}
            onClick={handleProfileClick}
            sx={{ cursor: 'pointer' }}
          >
            <NavIconSlot>
              <DarkTooltip title={collapsed ? 'Profile' : ''} placement="right">
                <span>
                  <SidebarAvatar>{getInitial()}</SidebarAvatar>
                </span>
              </DarkTooltip>
            </NavIconSlot>
            <SidebarRowLabel collapsed={collapsed} truncate>
              <ProfileSection account={account} />
            </SidebarRowLabel>
          </SidebarStaticRow>
        </Stack>
      </SidebarColumn>

      {modals}
    </ChatPageSidebarContainer>
  );
};
